















import { VNode } from 'vue';
import { Component, Prop, Provide, Watch } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import { ItemSelectRequestData, ItemExpandRequestData, ProvidedListParam, InternalItemHieDescriptor } from './types';
import SwTreeListItem from './sw-tree-list-item.vue';
import { utilService } from '../util-service';

/**
 * Super flexible tree list which holds no business logics internal.
 * Only styles are controlled by these components, and styles are
 * also set according to input props.
 *
 * This design makes the tree list as flexible as possible, as real
 * applications would have very different requirements for the list
 * behavior and one single list component can hardly satisfy all these
 * needs.
 *
 * So in the real case of usage, developers can create another reusable
 * list component encapsulating this tree list along with logics specific
 * for that application.
 *
 * Events:
 * @event request-select `(data: ItemSelectRequestData)`
 * @event request-expand `(data: ItemExpandRequestData)`
 */
@Component
export default class SwTreeList extends SwExtendedVue {
  @Prop({ type: Array, required: true })
  selected: string[];

  @Prop({ type: Array, default() { return []; } })
  expanded: string[];

  @Prop({ type: Array, default() { return [10, 10]; } })
  initialPadding: number[];

  @Prop({ type: Boolean, default: false })
  itemOverflow: boolean;

  @Prop({ type: String, default: 'arrow' })
  triggerStyle: 'arrow' | 'plus-minus';

  /** indicate this list is not used for tree, but only for plain flat list */
  @Prop({ type: Boolean, default: false })
  flat: boolean;

  @Prop({ type: Number, default: 16 })
  indent: number;

  @Prop({ type: Boolean, default: false })
  withItemDivider: boolean;

  @Provide()
  private listParam: ProvidedListParam = {
    selectedItems: this.selected,
    expandedItems: this.expanded,
    itemOverflow: this.itemOverflow,
    triggerStyle: this.triggerStyle,
    flat: this.flat,
    indent: this.indent,
    withItemDivider: this.withItemDivider,
    /** below are not reactive */
    initialPadding: this.initialPadding,
  };

  @Provide()
  private claimItemSelecting(itemId: string, selecting: boolean) {
    this.$emit('request-select', {
      itemId,
      selecting,
    } as ItemSelectRequestData);
  }

  @Provide()
  private claimParentExpanding(itemId: string, expanding: boolean) {
    this.$emit('request-expand', {
      itemId,
      expanding,
    } as ItemExpandRequestData);
  }

  @Watch('selected')
  private syncSelected(val: string[]) {
    this.listParam.selectedItems = val;
  }

  @Watch('expanded')
  private syncExpanded(val: string[]) {
    this.listParam.expandedItems = val;
  }

  @Watch('itemOverflow')
  private syncItemOverflow(val: boolean) {
    this.listParam.itemOverflow = val;
  }

  @Watch('triggerStyle')
  private syncTriggerStyle(val: ProvidedListParam['triggerStyle']) {
    this.listParam.triggerStyle = val;
  }

  @Watch('flat')
  private syncFlat(val: boolean) {
    this.listParam.flat = val;
  }

  @Watch('indent')
  private syncIndent(val: number) {
    this.listParam.indent = val;
  }

  @Watch('withItemDivider')
  private syncWithItemDivider(val: boolean) {
    this.listParam.withItemDivider = val;
  }

  /**
   * I know this will render children items twice at initial rendering and in some update cases,
   * but I have no other better solutions for now.
   */
  private updateItems() {
    function updateItemInstance(itemInst: SwTreeListItem, hieDescriptor: InternalItemHieDescriptor, hasNextSibling: boolean) {
      itemInst.hieDescriptor = hieDescriptor;

      traverseChildren(itemInst, hieDescriptor, hasNextSibling);
    }

    function countItems(children: VNode[]): number {
      return children.filter(child => (child.componentInstance && child.componentInstance.$options.name === 'SwTreeListItem')).length;
    }

    function traverseChildren(inst: SwTreeList | SwTreeListItem, hieDescriptor: InternalItemHieDescriptor | null, hasNextSibling: boolean) {
      const slot = utilService.getVueScopedSlot(inst.$scopedSlots);
      if (slot.length > 0) {
      // if (inst.$slots.default) {
        // const count = countItems(inst.$slots.default);
        const count = countItems(slot);
        let itemProcessed = 0;
        // inst.$slots.default.forEach((node) => {
        slot.forEach((node) => {
          if (node.componentInstance && node.componentInstance.$options.name === 'SwTreeListItem') {
            const itemInst = node.componentInstance as SwTreeListItem;
            const isLast = itemProcessed >= count - 1;
            updateItemInstance(
              itemInst,
              {
                level: hieDescriptor ? (hieDescriptor.level + 1) : 1,
                ancestorsNextSiblingFlags: hieDescriptor ? hieDescriptor.ancestorsNextSiblingFlags.concat(hasNextSibling) : [],
                isLastSibling: isLast,
              },
              count > 1 && !isLast,
            );
            itemProcessed += 1;
          }
        });
      }
    }

    traverseChildren(this, null, false);
  }

  private mounted() {
    this.$nextTick(() => {
      this.updateItems();
    });
  }

  private updated() {
    this.$nextTick(() => {
      this.updateItems();
    });
  }
}
